import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { HERO_PRODUCT, fmtPrice, waLink, displayName } from "@/data/site";
import { WatchDial } from "./WatchDial";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "10%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-emerald text-ivory flex items-center"
    >
      {/* Spotlight + grain */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_75%_40%,rgba(250,248,245,0.07),transparent_70%)]" />
      <div className="grain-overlay" />

      {/* Watermark */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none select-none">
        <p className="font-serif text-[22vw] leading-[0.8] font-semibold text-outline-ivory whitespace-nowrap translate-y-[18%]">
          ATILMAZ
        </p>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Copy */}
        <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">
          <MaskedLine delay={0.35}>
            <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-ivory/60">
              Atılmaz Saat — 2006'dan Beri Saat Sektöründe
            </span>
          </MaskedLine>

          <h1 className="mt-6 font-serif font-semibold tracking-tight leading-[1.02] text-[13.5vw] sm:text-6xl lg:text-7xl xl:text-[5.25rem]">
            <MaskedLine delay={0.5}>Zamana</MaskedLine>
            <MaskedLine delay={0.62}>
              <span className="italic text-gold">Değer</span> Katar.
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.85}>
            <span className="mt-7 block max-w-md text-sm sm:text-base leading-relaxed text-ivory/70 font-light">
              Kol saati, gözlük ve takıda özenle seçilmiş koleksiyonlar;
              teknik serviste güvenilir ustalık. Orijinallik garantisiyle,
              tek çatı altında.
            </span>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 1.05 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => scrollToId("#cok-satanlar")}
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-ivory px-8 h-[52px] text-sm font-semibold text-emerald transition-colors duration-300 hover:bg-sand"
            >
              Koleksiyonu Keşfet
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-ivory/25 px-8 h-[52px] text-sm font-medium text-ivory transition-colors duration-300 hover:border-ivory/60"
            >
              <MessageCircle size={16} />
              WhatsApp'tan Sor
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-12 flex items-center gap-8 text-ivory/50"
          >
            {[["2006", "Saat Sektöründe"], ["20", "Yıllık Tecrübe"], ["%100", "Orijinal Ürün"]].map(([v, l]) => (
              <div key={l}>
                <p className="font-serif text-2xl sm:text-3xl font-semibold text-ivory">{v}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em]">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Gerçek ürün spotlight — clipped ivory frame + parallax */}
        {HERO_PRODUCT && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <motion.div style={{ y: imgY }} className="relative aspect-[3/4] max-w-[300px] sm:max-w-[420px] mx-auto lg:ml-auto lg:mr-0">
              <div className="absolute -inset-3 border border-ivory/20 translate-x-4 translate-y-4" />
              <div
                data-testid="hero-visual-panel"
                className="relative block h-full w-full bg-emerald-deep overflow-hidden"
              >
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_35%,rgba(197,160,89,0.12),transparent_70%)]" />
                <div className="grain-overlay" />
                <WatchDial className="absolute inset-0 m-auto w-[74%] max-w-[360px] drop-shadow-2xl" />
                <p className="absolute top-5 left-0 right-0 text-center font-mono text-[10px] uppercase tracking-[0.35em] text-ivory/35">
                  Zamana Değer Katar
                </p>
                <p className="absolute bottom-5 left-0 right-0 text-center font-mono text-[10px] uppercase tracking-[0.35em] text-ivory/35">
                  Kol Saati • Gözlük • Takı
                </p>
              </div>
              <a
                href={HERO_PRODUCT.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-product-caption"
                className="absolute -bottom-5 -left-5 right-6 sm:right-auto bg-emerald-deep border border-ivory/15 text-ivory px-5 py-4 shadow-2xl block group"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/50">{HERO_PRODUCT.brand}</p>
                <p className="font-serif text-lg font-semibold leading-tight mt-1 line-clamp-1">{displayName(HERO_PRODUCT.name)}</p>
                <p className="mt-1.5 flex items-center gap-2 text-sm">
                  <span className="text-ivory font-semibold">{fmtPrice(HERO_PRODUCT.price)}</span>
                  <span className="inline-flex items-center gap-1 text-ivory/50 text-xs transition-colors group-hover:text-ivory">
                    İncele <ArrowUpRight size={12} />
                  </span>
                </p>
              </a>
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Scroll cue */}
      <motion.button
        onClick={() => scrollToId("#cok-satanlar")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        style={{ opacity: fade }}
        data-testid="hero-scroll-cue"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-ivory/40 hover:text-ivory transition-colors"
        aria-label="Aşağı kaydır"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Keşfet</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.button>
    </section>
  );
};
